<!--
  <TableTable
    :qunits="QUnitCollection()"
    :filters="QFilters()"
    :loading="true"
    @update="my_filter_update_handler(QFilters())"
    @update-qunit="my_qunit_update_handler(new_qunit)"
    @click="qunit_clicked(qunit)"
    :view_props.header_slots='["my_header1", "my_header2"]'
    view_props.expandable
    view_props.selectable
    view_props.multiselect
    view_props.actions
    :view_props.action_list='["Action1", "Action2"]'
  />

    # Public methods:
    - select_reset(index)
      // Reset selection of qunits
    - select_add(index)
      // Also select qunit
    - select_set(index)
      // Select only the given qunit
    - select_quint(uid)
      // Select only the given qunit by its uid
    - export_state() → {state}
      // Returns an Object representing the state of the table
      // This is used for saving: exported, selected.
    - import_state(state)
      // Imports an state exported by export_state

    # Slots
    - rows {view_props}
    - expanded {item}
    - pagination
    - *headers* → Must pass the list as view_props.header_slots parameter

-->

<template>
  <div class="qmkey-explorer-view">
    <div class="overflow-hidden" align="center" >
      <slot name="loading">
        <div v-if="loading">
          <v-progress-linear
            v-if="qunits"
            indeterminate
            color="primary"
          />
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
            class="mt-5 qmkey-progress-circular"
          />
        </div>
      </slot>
    </div>
    <v-data-table
      v-if="qunits && qunits.length > 0"
      :class="[is_mobile ? 'mobile' : '',view_props.table_class]"
      :headers="columns"
      :items="qunits.units"
      item-key="instance_id"
      :show-select="view_props.checkbox && view_props.selectable && view_props.multiselect"
      hide-default-footer
      must-sort
      :search="search_text"
      :server-items-length="server_items_count"
      :items-per-page="local_filters.get_filter_value('units_per_page')"
      :page="local_filters.get_filter_value('page')"
      :sortBy="local_filters.get_filter_value('sort_by')"
      :sortDesc="local_filters.get_filter_value('sort_desc')"
      v-on:update:sort-by="update_sort_by"
      v-on:update:sort-desc="update_sort_desc"
      dense
      item-class="qmkey-item-row"
      loading-text="Cargando..."
      class="qmkey-explorer-table table-contrast-expand-icon mt-0"
      :header-props="{'sort-icon': sort_icon}"
      :disable-sort="disabled || loading"
      :disable-pagination="disabled || loading"
      :disable-filtering="disabled || loading"
      :mobile-breakpoint="1"
      :expanded="expanded_units(qunits)"
      :custom-sort="(items) => {return items}"
      :key="is_mobile"
      @toggle-select-all="all_unit_select"
    >
      <template v-for="name in view_props.header_slots" v-slot:[`header.${name}`]="{header}">
        <slot :name='name' :header='header'>
          <span :key="header.value" class="none">
            {{ header.text }}
          </span>
        </slot>
      </template>
      <template v-slot:header.data-table-select>
        <v-btn icon :disabled="disabled" @click="all_unit_select">
          <v-icon color="primary">
            {{select_icon}}
          </v-icon>
        </v-btn>
      </template>
  
      <!-- item, expand, isExpanded, select, isSelected -->
      <template v-slot:item="props">
        <slot
          name="rows"
          :props="props"
        >
          <TableRow
            :unit="props.item"
            :qunits="qunits"
            :columns="columns"
            :expand="(state) => expand_set(props.index, state)"
            :select="props.select"
            :isExpanded="expanded[props.index]"
            :isSelected="object_selected[props.item.uid]"
            @update="(filters) => update_row(props.item, filters)"
            @update-qunit="update_qunit"
            @update-qunit-at="update_after_transition"
            @navigate_to="navigate_to(props.item, props)"
            @action-click="unit_select(props)"
            @click="qunit_clicked(props.item, props)"
            :expandable="view_props.expandable"
            :checkbox="view_props.checkbox && view_props.selectable && view_props.multiselect"
            :actions="view_props.actions"
            :disabled="disabled || loading"
            :action_list="view_props.action_list"
            :navigate="view_props.navigate"
            :is_mobile="is_mobile"
          />
        </slot>
      </template>
  
      <!-- Expanded -->
      <template v-slot:expanded-item="{ item }">
        <td :colspan="columns.length + !!view_props.multiselect + !!view_props.expandable - 1"
          class="qmkey-explorer-table-expansion-td"
        >
          <slot name="expanded" :item="item">
            <InlineViewer
              :unit="item"
              :ref="'inlineviewer_' + item.uid"
              @update="update_after_transition"
            />
          </slot>
        </td>
      </template>
  
      <!-- Pagination -->
      <template v-slot:footer v-if="qunits && qunits.length > 0">
        <slot name="footer">
          <QPagination
            :max_visible_buttons="3"
            :current_page="local_filters.get_filter_value('page')"
            :total_units="qunits.count"
            allow_change_items_per_page
            :items_per_page="local_filters.get_filter_value('units_per_page')"
            :units_length="qunits.length"
            @page_changed="change_page"
            @items_per_page_changed="change_units_per_page"
          />
        </slot>
      </template>
      <!-- /Pagination -->
    </v-data-table>
    <!-- NO DATA -->
    <!--<slot name="no-data">-->
      <div
        v-if="!loading && (!qunits || !(qunits.length > 0))"
        class="mt-5"
      >
        {{ $vuetify.lang.translator('explorer.no_data') }}
      </div>
    <!--</slot>-->
  </div>
</template>
  
  
<script>
  import QUnitCollection from '@/qunit/QUnitCollection'
  import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
  import InlineViewer from '@/components/explorer/inline/InlineViewer'
  import QPagination from '@/components/QPagination'
  import TableRow from './TableRow.vue';
  import { QFilters, get_qfilter } from '@/qunit/filters';
  
  function true_indexes(vec) {
    // vec → [false, false, true]
    // res → [index]
    const res = [];
    for (let i = 0; i < vec.length; i++) {
      if (vec[i]) {
        res.push(i);
      }
    }
    return res;
  }
 
  export default {
    name: 'TableTable',
 
    props: {
      qunits: QUnitCollection,
      filters: {type: QFilters, default: () => new QFilters()},
      loading: Boolean,
      view_props: {default: (()=> ({})) },
      checkbox: {type: Boolean, default: false},
      already_selected: {default: () => ([])},
      table_class: {type: String,default: ''},
      disabled: Boolean,
    },
 
    components: {
      InlineViewer,
      QPagination,
      TableRow,
    },
  
    data: () => ({
      local_filters: new QFilters(),
      object_selected: {}, // {id1: true, id2: false, id3: false, ...} use like selected[id]
      expanded: [], // [false, false, true] use like expanded[index]
      inline_states: {}, // {uid: inline.export_state()}
      all_selected: false,
    }),
  
    computed: {
      ...mapGetters(['sitePath', 'user']),
      ...mapState(['is_mobile',]),
 
      sort_icon() {
        return this.local_filters.get_filter_value('sort_desc') ? 'mdi-sort-descending' : 'mdi-sort-ascending';
      },
 
      columns() {
        return this.qunits ? this.qunits.columns.filter(
                  col => (this.view_props.actions || col.value != 'action' || col.value != 'data-table-expand')
                  ) 
                  : {};
      },
 
      server_items_count() {
       return this.view_props.server_pagination ? (this.qunits || {}).count : -1;
      },

      search_text() {
        // WARNING 
        // If local_search props is true the table will filter the values using header attr with the filtrable prop as true
        // If there is not a header with the filtrable props as true, it will show nothing
        return (this.props || {}).local_search ? this.filters.get_filter_value('search') : '';
      },

      select_icon() {
        const object_values = this.object_selected;
        const selected_values = Object.values(object_values)
        if (this.all_selected) {
          return 'mdi-checkbox-marked-outline';
        } else {
          return selected_values.indexOf(true) >= 0 ? 'mdi-minus-box-outline' : 'mdi-checkbox-blank-outline';
        }
      },
    },
 
    mounted() {
      this.setup();
      this.pull_filters();
    },
  
    methods: {
      setup() {
        if (this.qunits) {
          this.expand_reset();
          this.select_start();
        }
      },
  
      expand_reset() {
        this.expanded = new Array(this.qunits.length).fill(false);
      },
  
      select_reset() {
        this.object_selected = {};
      },
  
      select_start() {
        if (this.qunits) {
          this.select_reset();
          if (this.already_selected && this.qunits.length > 0) {
            for (let uid of this.already_selected) {
              this.select_set(uid);
            }
            this.all_selected = this.already_selected.length == this.qunits.count;
          }
        }
      },
      
      /* Qunits */
      
      expanded_units(qunits) {
        // Returns [QUnit(), QUnit()] calculated from this.expanded
        let expanded = [];
        if (qunits) {
          expanded = true_indexes(this.expanded).map(i => qunits.units[i])
        }
        return expanded;
      },
 
      pull_filters() {
        if (this.qunits) {
          //this.select_qunit(this.filters.pk);
        }
        this.local_filters = this.filters.copy();
      },
 
      /* UPDATES */
      launch_update() {
        this.$emit('update_filters', this.local_filters);
      },
 
      update_row(unit, filters) {
        this.local_filters.merge(filters);
        this.select_qunit(unit.uid);
        this.$emit('update', this.local_filters);
      },
  
      update_qunit(new_qunit) {
        this.select_qunit(new_qunit.uid);
        let state = this.export_state();
        this.$emit('update-qunit', new_qunit);
        this.$nextTick(()=> {
          this.import_state(state);
        })
      },
  
      update_after_transition(qunit) {
        if (qunit) {
          this.pull_inline_state(qunit.uid);
        }
        this.update_qunit(qunit);
      },
  
      navigate_to(qunit, row_props){
        this.unit_select(row_props);
        this.$emit('navigate_to', qunit);
      },
  
      qunit_clicked(qunit, row_props) {
        this.unit_select(row_props);
        this.$emit('click', qunit);
      },

      /* SELECTION */
      select_set(uid) {
        if (this.view_props.multiselect) {
          this.object_selected = {...this.object_selected,[uid]: !this.object_selected[uid]};
          
        } else if (!this.object_selected[uid]) {
          this.object_selected = {[uid]: true};
        }
        this.all_selected = Object.values(this.object_selected).length == this.qunits.count;
        this.$emit('update_selection',Object.entries(this.object_selected).filter(([_,value]) => !!value).map(([key,_]) => key));
      },
  
      select_add(index) {
      },

      select_qunit(uid) {
        if (uid) {
          this.select_set(uid);
        }
      },

     unit_select(row_props) {
        if (this.view_props.selectable) {
          if (row_props.item.uid) {
            this.select_set(row_props.item.uid);
          }
        }
      },

      all_unit_select() {
        this.all_selected = !this.all_selected;
        if (this.all_selected) {
          this.object_selected = {...this.object_selected,...Object.fromEntries(this.qunits.units.map(unit => [unit.uid,true]))}
        } else {
          this.object_selected = {};
        }
        
        this.$emit('select_all',this.all_selected)
      },

     update_sort_by(key) {
       if (key != this.local_filters.get_filter_value('sort_by')) {
         this.local_filters.set_filter(get_qfilter('sort_by',key));
         this.launch_update();
       }
     },

     update_sort_desc(sort) {
       if (sort != this.local_filters.get_filter_value('sort_desc')) {
         this.local_filters.set_filter(get_qfilter('sort_desc',sort));
         this.launch_update();
       }
     },

     change_page(pnum) {
       this.local_filters.set_filter(get_qfilter('page',pnum),true);
       this.launch_update();
     },

     change_units_per_page(units) {
      this.local_filters.set_filter(get_qfilter('units_per_page',units));
      this.local_filters.set_filter(get_qfilter('page',1),true);

       this.launch_update();
     },

     expand_set(index, state) {
       this.$set(this.expanded, index, !!state);
      
       const uid = this.qunits.units[index].uid;
       if (state) {
         this.$nextTick(() => this.push_inline_state(uid));
         if (uid) {
           this.select_set(uid);
         }
       } else {
         this.pull_inline_state(uid);
       }
     },

     export_state() {
       // Returns current table state
       return {
         expanded: true_indexes(this.expanded).map(i => this.qunits.units[i].uid),
         selected: this.object_selected,
         inlines: this.inline_states,
       };
     },

     import_state(state) {
       // Import an state exported by export_state()
       // this.expanded = (state.expanded
        function uid_to_trues(qunits, uids) {
          // uids → ['UID1', 'UID2']
          // Returns → [false, false, true]
          const res = new Array(qunits.length).fill(false);
          for (const uid of uids) {
            const pos = qunits.indexOf(uid);
            if (pos >= 0) {
              res[pos] = true;
            }
          }
          return res
        }
  
        // inline_states
        this.inline_states = state.inlines ? state.inlines : {};
    
        // expanded
        this.expand_reset();
        ((state.expanded ? state.expanded : [])
              .map(uid => this.qunits.indexOf(uid)) // [index1, index2]
              .filter(i => i >= 0)                  // Remove not found (-1)
              .forEach(index => this.expand_set(index, true)));
  
    
        // selected
        this.object_selected = state.selected || {};
      },
      
      /* INLINE STATE */
      push_inline_state(uid) {
        // Pushes the state to the inline of the qunit uid.
        // If no state exsits, does nothing
        if (!this.inline_states[uid]) {
          return;
        }
        if (this.$refs['inlineviewer_'+uid]) {
          this.$refs['inlineviewer_'+uid].import_state(this.inline_states[uid]);
        }
      },
 
      pull_inline_state(uid) {
        // Pulls the inline state and saves it into this.inline_states
        if (!this.$refs['inlineviewer_'+uid]) {
          return;
        }
        this.inline_states[uid] = this.$refs['inlineviewer_'+uid].export_state();
      },
    },
  
    watch: {
      filters() {
        this.pull_filters();
      },
  
      qunits(value) {
        // const state = this.export_state(); // To keep the state on qunits change.
        //                                       do we needed? I don't know yet ‾\_(ツ)_/‾
        this.setup();

        if (this.qunits) {
          const page = this.filters.get_filter_value('page') || this.qunits.page || 1;
          this.local_filters.set_filter(get_qfilter('page',page),true);
          let sort_by = this.local_filters.get_filter_value('sort_by')
          if (!sort_by) {
            sort_by = this.qunits.columns.findIndex(it => it.value == "current_workflow_limit") > 0 
                                  ? "current_workflow_limit" 
                                  : (this.qunits.columns[0] || {}).name
            this.local_filters.set_filter(get_qfilter('sort_by', sort_by))
          }
        }
        
        // this.import_state(state); // To keep the state on qunits change
      },
      is_mobile(mobile) {
        if(mobile) {
          this.expanded = this.expanded.map(() => false);
        }
      },
      already_selected() {
        this.object_selected = Object.fromEntries(this.already_selected.map(it => ([it,true])));
        this.all_selected = this.already_selected.length == (this.qunits || {}).count;
      },
    },
  }
</script>
  
  
  <style lang="scss">
   @import '@/sass/explorer.scss';
  </style>
  